import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import styled, { css } from 'styled-components'

import useOutsideClick from 'hooks/useOutsideClick'

type Props = {
  children: React.ReactNode | JSX.Element
  visible: boolean
  alignRight?: boolean
  alignTop?: boolean
  closeModal: () => void
}

const Overlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s;
  opacity: 0;
  visibility: hidden;
  z-index: 999;

  ${({ isVisible }) =>
    isVisible &&
    css`
      opacity: 1;
      visibility: visible;
    `}
`

const ChildrenWrapper = styled.div<{
  alignTop?: boolean
  alignRight?: boolean
}>`
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  ${({ alignTop }) =>
    alignTop &&
    css`
      align-items: flex-start;
    `}

  ${({ alignRight }) =>
    alignRight &&
    css`
      justify-content: flex-end;
    `}
`

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const Modal: React.FC<Props> = ({
  children,
  visible,
  alignTop,
  alignRight,
  closeModal,
}) => {
  useEffect(() => {
    const handleKeyboardNavigation = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal()
      }
    }
    window.addEventListener('keydown', handleKeyboardNavigation)
    return () => window.removeEventListener('keydown', handleKeyboardNavigation)
  }, [])

  const modalBodyRef = useRef(null)

  useOutsideClick({ ref: modalBodyRef, handler: closeModal })

  return ReactDOM.createPortal(
    <Overlay isVisible={visible}>
      <ChildrenWrapper alignTop={alignTop} alignRight={alignRight}>
        <ModalBody ref={modalBodyRef}>{children}</ModalBody>
      </ChildrenWrapper>
    </Overlay>,
    document.body
  )
}
export default Modal
